//-------------CORE-------------//
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
//-------------MUI-------------//
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
//----------EXTERNAL COMPS--------//
import devicesApi, {
  useGetImportableDevicesQuery,
  useImportDevicesMutation,
  useImportDevicesCsvMutation,
} from "services/devices";
import { statusFilters, statusOf } from "./deviceStatus";

// the server takes at most 500 devices per import; a bigger selection goes in parts of this size
const CHUNK = 200;
const CSV_MAX_BYTES = 2 * 1024 * 1024;
const CSV_TEMPLATE = "imei,serial,solution,name,assetType,latitude,longitude\r\n";
// the CSV file's columns, as the Upload CSV tab explains them: [column, needed, what goes in it, example]
const CSV_GUIDE = [
  ["imei", "Yes", "15 digits; must match exactly one device on the platform", "867929065923536"],
  ["serial", "Yes", "Serial number; if the platform already has one for the device (shown in the Available devices list) it must be the same, otherwise it is saved to the device", "2024010080"],
  ["solution", "Yes", "Solution name or id (one your role can see); rows can name different solutions", "Salesforce Munich"],
  ["name", "No", "Device name; empty keeps the platform name", "Lobby West"],
  ["assetType", "If the solution has several", "Asset type name or id", "Air Monitor"],
  ["latitude, longitude", "No (both or neither)", "Position; empty uses the last known one, else the solution's default", "51.5072, -0.1276"],
];
const columns = [
  { key: "name", label: "Name" },
  { key: "id", label: "ID" },
  { key: "imei", label: "IMEI" },
  { key: "iccid", label: "ICCID" },
  { key: "serial", label: "Serial Number" },
  { key: "firmware", label: "Firmware" },
  { key: "status", label: "Status" },
  { key: "lastMessage", label: "Last Message" },
  { key: "created", label: "Created" },
];
const formatDate = (value) => {
  const date = new Date(value);
  return value && !isNaN(date) ? `${date.toLocaleDateString("en-GB")} ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "-";
};
const emptyResult = () => ({ imported: [], skipped: [], failed: [] });

// Imports devices that exist on the tenant's platform (Cumulocity) into a solution: pick them from
// a searchable, paged list, or upload a CSV file (checked row by row before anything is imported).
export default function ImportDevices({ open, onClose }) {
  const token = window.localStorage.getItem("token");
  const dispatch = useDispatch();
  const solutions = useSelector((state) => state.metaData?.services) || [];
  const [tab, setTab] = useState("platform");
  const [serviceId, setServiceId] = useState("");
  const [assetType, setAssetType] = useState("");
  const solution = solutions.find((s) => s.id == serviceId);
  const assets = solution?.assets || [];
  const needsAssetType = assets.length > 1 && !assetType;

  // ---- the platform list -----------------------------------------------------------------------
  const [tempSearch, setTempSearch] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState({ key: "created", order: "desc" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState(new Set());
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [importDevices] = useImportDevicesMutation();
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(tempSearch.trim());
    }, 600);
    return () => clearTimeout(timer);
  }, [tempSearch]);
  const filterParams = (extra) =>
    new URLSearchParams({ sort: sort.key, order: sort.order, ...(search ? { search } : {}), ...(status ? { status } : {}), ...extra }).toString();
  const list = useGetImportableDevicesQuery(
    { token, params: filterParams({ pageSize: String(pageSize), currentPage: String(page + 1) }) },
    { skip: !open }
  );
  const payload = list.currentData?.payload;
  const rows = payload?.data || [];
  const total = payload?.totalDocuments || 0;

  // a one-off read of the list endpoint (subscribe, take the result, let go of it)
  async function readOnce(params) {
    const request = dispatch(devicesApi.endpoints.getImportableDevices.initiate({ token, params }, { forceRefetch: true }));
    try {
      return await request.unwrap();
    } finally {
      request.unsubscribe();
    }
  }
  async function refresh() {
    setError("");
    try {
      await readOnce(filterParams({ refresh: "true", pageSize: "25" }));
      list.refetch();
    } catch (err) {
      setError(err?.data?.message || "Could not read the platform's devices");
    }
  }
  async function selectAllMatching() {
    try {
      const all = await readOnce(filterParams({ idsOnly: "true" }));
      setSelected((prev) => new Set([...prev, ...all.payload.ids]));
    } catch (err) {
      setError(err?.data?.message || "Could not select the matching devices");
    }
  }
  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const pageSelected = rows.length > 0 && rows.every((d) => selected.has(d.id));
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((d) => (pageSelected ? next.delete(d.id) : next.add(d.id)));
      return next;
    });
  const sortBy = (key) => {
    setPage(0);
    setSort((prev) => ({ key, order: prev.key === key && prev.order === "asc" ? "desc" : "asc" }));
  };

  async function importSelected() {
    const ids = [...selected];
    const summary = emptyResult();
    let stoppedAt = ids.length;
    setError("");
    setResult(null);
    setProgress({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i += CHUNK) {
      const response = await importDevices({
        token,
        body: {
          devices: ids.slice(i, i + CHUNK).map((id) => ({ id })),
          serviceId,
          location: solution?.defaultLocation,
          platformDeviceType: assetType || undefined,
        },
      });
      if (response.error) {
        setError(`${response.error.data?.message || "Import failed"}${i ? ` (after ${i} of ${ids.length} devices)` : ""}`);
        stoppedAt = i;
        break;
      }
      const part = response.data.payload;
      summary.imported.push(...part.imported);
      summary.skipped.push(...part.skipped);
      summary.failed.push(...part.failed);
      setProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
    }
    setProgress(null);
    if (summary.imported.length + summary.skipped.length + summary.failed.length) setResult(summary);
    // what is left to do stays selected: the devices not tried after a failed part, and the failed
    setSelected(new Set([...ids.slice(stoppedAt), ...summary.failed.map((d) => d.id)]));
    list.refetch();
  }

  // ---- the CSV upload --------------------------------------------------------------------------
  const [file, setFile] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [csvReport, setCsvReport] = useState(null);
  const [validated, setValidated] = useState(false);
  const [importCsv, csvState] = useImportDevicesCsvMutation();
  const [showFormat, setShowFormat] = useState(true);
  useEffect(() => setValidated(false), [csvText]);
  async function pickFile(e) {
    const picked = e.target.files && e.target.files[0];
    e.target.value = "";
    setCsvReport(null);
    if (!picked) return;
    if (picked.size > CSV_MAX_BYTES) {
      setFile(null);
      setCsvText("");
      setCsvReport({ error: "The file is larger than 2 MB." });
      return;
    }
    setFile(picked);
    setCsvText(await picked.text());
  }
  async function sendCsv(dryRun) {
    setCsvReport(null);
    // the file names each row's solution, so no default is sent
    const response = await importCsv({ token, body: { csv: csvText, dryRun } });
    if (response.error) {
      const body = response.error.data || {};
      setShowFormat(false);
      setCsvReport({ error: body.message || "The file could not be checked", formatErrors: body.payload?.errors, rows: body.payload?.rows });
      return;
    }
    const body = response.data;
    setShowFormat(false);
    setCsvReport({ message: body.message, result: dryRun ? null : body.payload });
    setValidated(dryRun);
    if (!dryRun) {
      setFile(null);
      setCsvText("");
      list.refetch();
    }
  }
  function downloadTemplate() {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" }));
    link.download = "Device import template.csv";
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  // ---- shared pieces ---------------------------------------------------------------------------
  const solutionPicker = (
    <div style={{ display: "flex", gap: "10px", minWidth: 0, flex: "1 1 360px" }}>
      <FormControl size="small" fullWidth>
        <InputLabel id="import-solution">Import into solution *</InputLabel>
        <Select
          labelId="import-solution"
          label="Import into solution *"
          value={serviceId}
          onChange={(e) => {
            setServiceId(e.target.value);
            setAssetType("");
          }}
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
        >
          {solutions.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {tab === "platform" && assets.length > 1 ? (
        <FormControl size="small" fullWidth>
          <InputLabel id="import-asset-type">Asset type *</InputLabel>
          <Select labelId="import-asset-type" label="Asset type *" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
            {assets.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </div>
  );
  // "One: 2, Three: 1" - the devices a CSV import put into each solution
  const perSolution = (result) => {
    const imported = new Set(result.imported.map((d) => d.id));
    const counts = {};
    (result.rows || []).filter((r) => imported.has(r.deviceId)).forEach((r) => (counts[r.solution] = (counts[r.solution] || 0) + 1));
    return Object.entries(counts).map(([name, n]) => `${name}: ${n}`).join(", ");
  };
  // labelOf: how a device is named in the list (the CSV tab names devices by IMEI)
  const outcome = (summary, labelOf = (id) => id) => (
    <Alert severity={summary.failed.length ? "warning" : "success"} style={{ marginBottom: "10px" }}>
      {summary.imported.length} imported
      {summary.skipped.length ? `, ${summary.skipped.length} already in Xelerate` : ""}
      {summary.failed.length ? `, ${summary.failed.length} failed` : ""}.
      {[...summary.failed, ...summary.skipped].slice(0, 20).map((d) => (
        <div key={d.id} style={{ fontSize: "12px" }}>
          {labelOf(d.id)}: {d.reason}
        </div>
      ))}
    </Alert>
  );

  return (
    <Dialog open={open} onClose={progress || csvState.isLoading ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Devices</DialogTitle>
      <Tabs value={tab} onChange={(e, value) => setTab(value)} style={{ padding: "0 24px" }}>
        <Tab value="platform" label="Available devices" />
        <Tab value="csv" label="Upload CSV" />
      </Tabs>
      <DialogContent style={{ height: "65vh", display: "flex", flexDirection: "column", gap: "10px", paddingTop: "15px" }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {tab === "platform" ? (
          <>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "5px" }}>
              {solutionPicker}
              <TextField
                size="small"
                label="Search"
                placeholder="Name, ID, IMEI, ICCID, serial, firmware"
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                style={{ flex: "1 1 260px" }}
              />
              <FormControl size="small" style={{ width: "170px" }}>
                <InputLabel id="import-status" shrink>
                  Status
                </InputLabel>
                <Select
                  labelId="import-status"
                  label="Status"
                  value={status}
                  displayEmpty
                  notched
                  onChange={(e) => {
                    setPage(0);
                    setStatus(e.target.value);
                  }}
                >
                  {statusFilters.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title={payload?.fetchedAt ? `List read at ${formatDate(payload.fetchedAt)}; read it again` : "Read the list again"} arrow>
                <span>
                  <IconButton id="import-refresh" onClick={refresh} disabled={list.isFetching}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
            {result ? outcome(result) : null}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", minHeight: "30px", fontSize: "13px", color: "#555" }}>
              <span id="import-selection">{selected.size} selected</span>
              {total > 0 ? (
                <Button size="small" onClick={selectAllMatching}>
                  Select all {total} matching
                </Button>
              ) : null}
              {selected.size ? (
                <Button size="small" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              ) : null}
            </div>
            {list.isFetching ? <LinearProgress /> : null}
            {list.isError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => list.refetch()}>
                    Retry
                  </Button>
                }
              >
                {list.error?.data?.message || "Could not read the platform's devices"}
              </Alert>
            ) : null}
            <TableContainer style={{ flex: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        id="import-select-page"
                        checked={pageSelected}
                        indeterminate={!pageSelected && rows.some((d) => selected.has(d.id))}
                        onChange={togglePage}
                        disabled={!rows.length}
                      />
                    </TableCell>
                    {columns.map((c) => (
                      <TableCell key={c.key} style={{ whiteSpace: "nowrap" }}>
                        <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key ? sort.order : "asc"} onClick={() => sortBy(c.key)}>
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((d) => {
                    const st = statusOf(d.status);
                    return (
                      <TableRow key={d.id} hover onClick={() => toggle(d.id)} style={{ cursor: "pointer" }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selected.has(d.id)} />
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{d.name || "-"}</TableCell>
                        <TableCell>{d.id}</TableCell>
                        <TableCell>{d.imei || "-"}</TableCell>
                        <TableCell>{d.iccid || "-"}</TableCell>
                        <TableCell>{d.serial || "-"}</TableCell>
                        <TableCell>{d.firmware || "-"}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap", color: st.color }}>{st.label}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{formatDate(d.lastMessage)}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{formatDate(d.created)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!list.isFetching && !list.isError && !rows.length ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" style={{ color: "#999", padding: "40px" }}>
                        {search || status ? "No platform devices match." : "Every platform device is already in Xelerate."}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={Math.min(page, Math.max(Math.ceil(total / pageSize) - 1, 0))}
              onPageChange={(e, value) => setPage(value)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onRowsPerPageChange={(e) => {
                setPage(0);
                setPageSize(Number(e.target.value));
              }}
            />
            {progress ? (
              <div>
                <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
                <p style={{ fontSize: "12px", color: "#555" }}>
                  Importing {progress.done} of {progress.total} devices ...
                </p>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "5px" }}>
              <Button variant="outlined" component="label">
                {file ? file.name : "Choose CSV file"}
                <input id="import-csv-file" type="file" accept=".csv,text/csv" hidden onChange={pickFile} />
              </Button>
              <Button onClick={downloadTemplate}>Download template</Button>
              <Button id="import-csv-format-toggle" onClick={() => setShowFormat((v) => !v)}>
                {showFormat ? "Hide file format" : "Show file format"}
              </Button>
            </div>
            {showFormat ? (
              <div id="import-csv-format">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Column</TableCell>
                      <TableCell>Needed</TableCell>
                      <TableCell>What goes in it</TableCell>
                      <TableCell>Example</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {CSV_GUIDE.map(([column, needed, what, example]) => (
                      <TableRow key={column}>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          <b>{column}</b>
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>{needed}</TableCell>
                        <TableCell>{what}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap", color: "#777" }}>{example}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ul style={{ fontSize: "13px", color: "#555", margin: "10px 0 0", paddingLeft: "20px", lineHeight: 1.7 }}>
                  <li>The first row names the columns (any order, capitals ignored); then one device per row.</li>
                  <li>UTF-8 text, comma-separated, up to 1,000 devices per file.</li>
                  <li>Validate checks every row first; if any row has a problem, nothing is imported.</li>
                </ul>
              </div>
            ) : null}
            {csvState.isLoading ? <LinearProgress /> : null}
            {csvReport?.message ? (
              csvReport.result ? (
                <>
                  {outcome(csvReport.result, (id) => ((csvReport.result.rows || []).find((r) => r.deviceId === id) || {}).imei || id)}
                  <div id="import-csv-by-solution" style={{ fontSize: "13px", color: "#555" }}>
                    {perSolution(csvReport.result)}
                  </div>
                </>
              ) : (
                <Alert severity="success">{csvReport.message}</Alert>
              )
            ) : null}
            {csvReport?.error ? <Alert severity="error">{csvReport.error}</Alert> : null}
            {csvReport?.formatErrors?.length ? (
              <TableContainer style={{ flex: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>Problem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvReport.formatErrors.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.line || "-"}</TableCell>
                        <TableCell>{e.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            {csvReport?.rows?.length ? (
              <TableContainer style={{ flex: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>IMEI</TableCell>
                      <TableCell>Serial</TableCell>
                      <TableCell>Solution</TableCell>
                      <TableCell>Problems</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvReport.rows
                      .filter((r) => r.errors.length)
                      .map((r) => (
                        <TableRow key={r.line}>
                          <TableCell>{r.line}</TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>{r.imei || "-"}</TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>{r.serial || "-"}</TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>{r.solution || "-"}</TableCell>
                          <TableCell>{r.errors.join("; ")}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!progress || csvState.isLoading}>
          Close
        </Button>
        {tab === "platform" ? (
          <Button
            id="import-selected"
            variant="contained"
            color="secondary"
            onClick={importSelected}
            disabled={!serviceId || needsAssetType || !selected.size || !!progress}
          >
            Import {selected.size || ""} {selected.size === 1 ? "device" : "devices"}
          </Button>
        ) : (
          <>
            <Button id="import-csv-validate" onClick={() => sendCsv(true)} disabled={!csvText || csvState.isLoading}>
              Validate
            </Button>
            <Button
              id="import-csv-import"
              variant="contained"
              color="secondary"
              onClick={() => sendCsv(false)}
              disabled={!csvText || !validated || csvState.isLoading}
            >
              Import
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
